"use client";

import {
  CircleCheck,
  CircleX,
  Info,
  ShieldX,
  TriangleAlert,
  WifiOff,
  Wrench,
  X,
  type LucideIcon,
} from "lucide-react";
import { useEffect, type HTMLAttributes, type ReactNode } from "react";
import { createPortal } from "react-dom";

import { cn } from "@/lib/utils/cn";
import { IconButton } from "./button";

export type FeedbackTone = "neutral" | "primary" | "success" | "warning" | "error" | "info" | "offline" | "maintenance";

const feedbackToneClass: Record<FeedbackTone, string> = {
  neutral: "border-card-border bg-surface-secondary text-foreground",
  primary: "border-[color-mix(in_srgb,var(--accent-primary)_45%,transparent)] bg-[var(--accent-primary)] text-[var(--accent-foreground)]",
  success: "border-status-success-border bg-status-success-surface text-status-success",
  warning: "border-status-warning-border bg-status-warning-surface text-status-warning",
  error: "border-status-error-border bg-status-error-surface text-status-error",
  info: "border-status-info-border bg-status-info-surface text-status-info",
  offline: "border-status-offline-border bg-status-offline-surface text-status-offline",
  maintenance: "border-status-maintenance-border bg-status-maintenance-surface text-status-maintenance",
};

const feedbackIcon: Record<FeedbackTone, LucideIcon> = {
  neutral: Info,
  primary: Info,
  success: CircleCheck,
  warning: TriangleAlert,
  error: CircleX,
  info: Info,
  offline: WifiOff,
  maintenance: Wrench,
};

export type AlertProps = HTMLAttributes<HTMLDivElement> & {
  tone?: FeedbackTone;
  title?: ReactNode;
  description?: ReactNode;
  action?: ReactNode;
  icon?: LucideIcon | null;
};

export function Alert({ tone = "info", title, description, action, icon, children, className, ...props }: AlertProps) {
  const Icon = icon === null ? null : icon ?? feedbackIcon[tone];
  return (
    <div role={tone === "error" ? "alert" : "status"} className={cn("flex items-start gap-ds-3 rounded-control border p-ds-4", feedbackToneClass[tone], className)} {...props}>
      {Icon ? <Icon className="mt-0.5 h-5 w-5 shrink-0" aria-hidden /> : null}
      <div className="min-w-0 flex-1">
        {title ? <p className="text-caption font-semibold text-current">{title}</p> : null}
        {description ? <p className={cn("text-caption leading-relaxed opacity-90", title && "mt-ds-1")}>{description}</p> : null}
        {children}
      </div>
      {action ? <div className="shrink-0">{action}</div> : null}
    </div>
  );
}

export type BannerProps = AlertProps;
export function Banner({ className, ...props }: BannerProps) {
  return <Alert className={cn("rounded-none border-x-0 px-[var(--spacing-page)]", className)} {...props} />;
}

export type ToastProps = {
  open: boolean;
  message: ReactNode;
  onClose: () => void;
  duration?: number;
  tone?: FeedbackTone;
  action?: ReactNode;
  className?: string;
};

export function Toast({ open, message, onClose, duration = 3200, tone = "neutral", action, className }: ToastProps) {
  useEffect(() => {
    if (!open || duration <= 0) return;
    const timer = window.setTimeout(onClose, duration);
    return () => window.clearTimeout(timer);
  }, [duration, onClose, open]);
  if (!open || typeof document === "undefined") return null;
  return createPortal(
    <div className="pointer-events-none fixed inset-x-0 top-[calc(var(--topbar-height)+0.75rem)] z-[1300] flex justify-center px-ds-4 sm:justify-end" role="region" aria-label="Notifications">
      <div className={cn("pointer-events-auto flex max-w-md items-center gap-ds-3 rounded-card border p-ds-3 shadow-overlay", feedbackToneClass[tone], className)} role={tone === "error" ? "alert" : "status"}>
        <span className="min-w-0 flex-1 text-caption font-medium">{message}</span>
        {action}
        <IconButton
          variant="ghost"
          aria-label="Dismiss notification"
          onClick={onClose}
          className={tone === "primary" ? "!text-[var(--accent-foreground)] hover:!bg-black/10 hover:!text-[var(--accent-foreground)]" : undefined}
        >
          <X className="h-4 w-4" />
        </IconButton>
      </div>
    </div>,
    document.body,
  );
}

export type SkeletonProps = HTMLAttributes<HTMLDivElement>;
export function Skeleton({ className, ...props }: SkeletonProps) {
  return <div className={cn("ds-skeleton rounded-control", className)} aria-hidden {...props} />;
}

export function SkeletonStatCard() {
  return (
    <div className="rounded-card border border-card-border bg-surface-primary p-[var(--density-panel-padding)] shadow-raised" aria-label="Loading statistic">
      <Skeleton className="h-3 w-24" /><Skeleton className="mt-ds-4 h-10 w-20" /><Skeleton className="mt-ds-3 h-3 w-full max-w-[12rem]" />
    </div>
  );
}

export function SkeletonTableRow({ cols = 4 }: { cols?: number }) {
  return <div className="flex min-h-[var(--density-row-comfortable)] items-center gap-ds-4 border-b border-card-border py-ds-3" aria-label="Loading row">{Array.from({ length: cols }).map((_, index) => <Skeleton key={index} className="h-4 flex-1" />)}</div>;
}

export type EmptyStateProps = {
  icon?: LucideIcon;
  title: string;
  description?: string;
  action?: ReactNode;
  className?: string;
};

export function EmptyState({ icon: Icon, title, description, action, className }: EmptyStateProps) {
  return (
    <div className={cn("flex flex-col items-center justify-center rounded-card border border-dashed border-card-border bg-surface-primary px-ds-6 py-ds-16 text-center shadow-none", className)} role="status">
      {Icon ? <div className="flex h-12 w-12 items-center justify-center rounded-full border border-card-border bg-surface-secondary text-muted"><Icon className="h-6 w-6" strokeWidth={1.5} aria-hidden /></div> : null}
      <p className={cn("text-title text-foreground", Icon && "mt-ds-4")}>{title}</p>
      {description ? <p className="mt-ds-2 max-w-md text-caption leading-relaxed text-muted">{description}</p> : null}
      {action ? <div className="mt-ds-6">{action}</div> : null}
    </div>
  );
}

export type ErrorStateProps = Omit<EmptyStateProps, "icon"> & { icon?: LucideIcon };
export function ErrorState({ icon = CircleX, ...props }: ErrorStateProps) {
  return <EmptyState icon={icon} {...props} className={cn("border-status-error-border bg-status-error-surface", props.className)} />;
}

export type NoAccessStateProps = Omit<EmptyStateProps, "icon"> & { icon?: LucideIcon };
export function NoAccessState({ icon = ShieldX, ...props }: NoAccessStateProps) {
  return <EmptyState icon={icon} {...props} />;
}
